import { useCallback, useMemo } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import reviewsClient, {
  CreateReviewInput,
  ReviewSearchInput,
  UpdateReviewInput,
} from "api/reviews";
import firebaseService from "firebase_service/firebaseService";
import { ReviewRequest } from "../types";

export const REVIEWS_QUERY_KEY = ["review-requests"];
export const REVIEW_DETAIL_QUERY_KEY = ["review-request-detail"];
const reviewDetailQueryKey = (id: string) => [...REVIEW_DETAIL_QUERY_KEY, id];

// The whole open set is small (tens of rows), so one page is enough; the
// closed view is capped at the API maximum and sorted newest first.
const PAGE_SIZE = 200;

export function useReviewSearch(
  input: ReviewSearchInput,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const query = useQuery({
    queryKey: [...REVIEWS_QUERY_KEY, input],
    queryFn: () => reviewsClient.search({ ...input, limit: PAGE_SIZE }),
    enabled,
    placeholderData: keepPreviousData,
  });
  return {
    requests: query.data?.requests ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}

// Deep links (`?review=<id>`) open even when the row sits outside the active
// quick view. `isError` lets the view say so when the link is dead.
export function useReviewById(id: string | null): {
  request: ReviewRequest | null;
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery({
    queryKey: reviewDetailQueryKey(id ?? ""),
    queryFn: () => reviewsClient.get(id as string),
    enabled: !!id,
    retry: 1,
  });
  return { request: data ?? null, isLoading: !!id && isLoading, isError };
}

// Badge on the Klydoscope tab: open requests waiting for the signed-in user.
export function usePendingReviewCount(): number {
  const uid = firebaseService.getUserId() ?? "";
  const { data } = useQuery({
    queryKey: [...REVIEWS_QUERY_KEY, "pending-count", uid],
    queryFn: () =>
      reviewsClient.search({
        statuses: ["open"],
        reviewerId: uid,
        limit: 1,
      }),
    enabled: !!uid,
    refetchInterval: 60_000,
  });
  return data?.total ?? 0;
}

export function useReviewMutations() {
  const queryClient = useQueryClient();

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: REVIEWS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: REVIEW_DETAIL_QUERY_KEY }),
    ]);
  }, [queryClient]);

  // Every action returns the fresh document from the API. It is written into
  // the detail cache (no second GET needed) and only the lists are refetched.
  const applyResult = useCallback(
    async (request: ReviewRequest) => {
      queryClient.setQueryData(reviewDetailQueryKey(request.id), request);
      await queryClient.invalidateQueries({ queryKey: REVIEWS_QUERY_KEY });
      return request;
    },
    [queryClient],
  );

  return useMemo(
    () => ({
      invalidate,
      create: async (input: CreateReviewInput) =>
        applyResult(await reviewsClient.create(input)),
      update: async (id: string, input: UpdateReviewInput) =>
        applyResult(await reviewsClient.update(id, input)),
      reRequest: async (id: string) =>
        applyResult(await reviewsClient.reRequest(id)),
      remind: async (id: string) => applyResult(await reviewsClient.remind(id)),
      snooze: async (id: string, hours: number) =>
        applyResult(await reviewsClient.snooze(id, hours)),
      markChecked: async (id: string, reviewerId?: string) =>
        applyResult(await reviewsClient.markChecked(id, reviewerId)),
      unmarkChecked: async (id: string, reviewerId?: string) =>
        applyResult(await reviewsClient.unmarkChecked(id, reviewerId)),
      cancel: async (id: string) => applyResult(await reviewsClient.cancel(id)),
    }),
    [applyResult, invalidate],
  );
}
